/* global google */
import React, { Component } from "react";
import { GMap } from "primereact/gmap";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { loadGoogleMaps, removeGoogleMaps } from "../load/GoogleMaps";
import { LOCATIONZOOM } from "../Enums";

export class AppMap extends Component {
  constructor(props) {
    super(props);
    this.myRef = React.createRef();

    const options =
      this.props.options ||
      {
        center: { lat: 23.797911, lng: 90.414391 },
        zoom: LOCATIONZOOM.ZOOM,
      };

    this.state = {
      googleMapsReady: false,
      dialogVisible: false,
      markerTitle: "",
      draggableMarker: false,
      overlays: [], // markers only
      selectedPosition: null,
      options,
      placeName: null,
      map: null,
    };

    // runtime helpers
    this._gmReady = false;
    this._pendingGeocodes = [];
    this._autocomplete = null;
    this._autocompleteListener = null;
    this._circleObjs = []; // store live circle instances

    this.onMapClick = this.onMapClick.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.handleDragEnd = this.handleDragEnd.bind(this);
    this.onMapReady = this.onMapReady.bind(this);
    this.onHide = this.onHide.bind(this);
    this.addMarker = this.addMarker.bind(this);
  }

  /** Public: safely geocode even if SDK or map isn't ready yet */
  getLatlong = (address, fn) => {
    const query = (address || "").trim();
    if (!query) return;

    const run = () => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: query }, (results, status) => {
        if (status === "OK" && results?.[0]?.geometry?.location) {
          const loc = results[0].geometry.location;
          const coords = { latitude: loc.lat(), longitude: loc.lng() };
          if (this.state.map) {
            const center = { lat: coords.latitude, lng: coords.longitude };
            this.state.map.setCenter(center);
            this.state.map.panTo(center);
          }
          fn?.(coords);
        } else {
          const center = this.state.map?.getCenter?.();
          fn?.({
            latitude: center?.lat?.() ?? this.state.options.center.lat,
            longitude: center?.lng?.() ?? this.state.options.center.lng,
          });
        }
      });
    };

    if (this._gmReady && window.google?.maps) {
      run();
    } else {
      this._pendingGeocodes.push(run);
    }
  };

  _initAutocomplete = () => {
    const input = document.getElementById("autocomplete");
    if (!input || !window.google?.maps?.places) return;
    if (this._autocomplete) return;

    this._autocomplete = new google.maps.places.Autocomplete(input, {
      types: ["geocode"],
    });

    this._autocompleteListener = this._autocomplete.addListener(
      "place_changed",
      () => {
        const place = this._autocomplete.getPlace();
        if (!place?.geometry?.location) return;

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const center = { lat, lng };

        if (this.state.map) {
          this.state.map.setCenter(center);
          this.state.map.panTo(center);
        }
        this.props?.changeCenterSearchText?.(
          place.formatted_address || input.value
        );
      }
    );
  };

  componentDidMount() {
    loadGoogleMaps(() => {
      this._gmReady = true;
      // render GMap, then onMapReady will give us the map object
      this.setState({ googleMapsReady: true });
    });
  }

  componentDidUpdate(prevProps, prevState) {
    // wait until we actually have a map
    if (!this._gmReady || !this.state.map) return;

    const overlaysChanged =
      prevProps.overlays !== this.props.overlays;
    const circlesChanged =
      prevProps.circles !== this.props.circles;
    const optionsChanged =
      prevProps.options !== this.props.options;

    if (overlaysChanged || optionsChanged) {
      this._rebuildMarkers();
    }
    if (circlesChanged || optionsChanged) {
      this._renderCircles(this.props.circles, this.state.map);
    }

    if (optionsChanged && this.props.options?.center) {
      const { center } = this.props.options;
      this.state.map.setCenter(center);
      this.state.map.panTo(center);
      if (typeof this.props.options.zoom === "number") {
        this.state.map.setZoom(this.props.options.zoom);
      } else {
        this.state.map.setZoom(LOCATIONZOOM.ZOOM);
      }
    }
  }

  componentWillUnmount() {
    this._clearMarkers();
    this._clearCircles();

    if (this._autocompleteListener) {
      google.maps.event.removeListener(this._autocompleteListener);
      this._autocompleteListener = null;
    }
    this._autocomplete = null;

    // optional (if you truly want to unload SDK)
    removeGoogleMaps();
  }

  /** ----------- Markers (via overlays) ----------- */
  _clearMarkers = () => {
    (this.state.overlays || []).forEach((o) => {
      try { o.setMap(null); } catch { }
    });
    // don't setState here to avoid re-render storm; caller will set overlays
  };

  _rebuildMarkers = () => {
    const map = this.state.map;
    if (!map) return;

    // clear previous marker overlays from map
    this._clearMarkers();

    const newOverlays = [];
    const src = Array.isArray(this.props.overlays) ? this.props.overlays : [];
    src.forEach((e) => {
      // expect { position: {lat, lng}, title? }
      const marker = new google.maps.Marker({ ...(e || {}), map });
      newOverlays.push(marker);
    });

    const nextOptions = this.props.options || this.state.options;
    const center = nextOptions?.center || this.state.options.center;
    if (center) {
      map.setCenter(center);
      map.panTo(center);
    }

    this.setState({ overlays: newOverlays, options: nextOptions });
  };

  /** ----------- Circles (managed directly) ----------- */
  _clearCircles = () => {
    this._circleObjs.forEach((c) => {
      try { c.setMap(null); } catch { }
    });
    this._circleObjs = [];
  };

  _renderCircles = (circles, map) => {
    this._clearCircles();
    if (!map || !Array.isArray(circles)) return;

    circles.forEach((c) => {
      const lat = Number(c?.center?.lat);
      const lng = Number(c?.center?.lng);
      const radius = Number(c?.radius);

      if (!isFinite(lat) || !isFinite(lng) || !isFinite(radius) || radius <= 0)
        return;

      const circle = new google.maps.Circle({
        center: new google.maps.LatLng(lat, lng),
        radius, // meters
        map,
        fillColor: "#1976D2",
        fillOpacity: 0.2,
        strokeColor: "#1976D2",
        strokeOpacity: 0.7,
        strokeWeight: 2,
        clickable: false,
      });

      this._circleObjs.push(circle);
    });
  };

  /** ----------- Events & Dialog helpers ----------- */
  onMapReady(e) {
    // PrimeReact passes map instance in event
    const map = e.map || this.myRef.current?.getMap?.();
    this.setState(
      {
        map,
        options: this.props.options || this.state.options,
      },
      () => {
        this._initAutocomplete();
        this._rebuildMarkers();
        this._renderCircles(this.props.circles, map);

        // flush any geocode calls queued before map existed
        if (this._pendingGeocodes.length) {
          const jobs = [...this._pendingGeocodes];
          this._pendingGeocodes = [];
          jobs.forEach((job) => job());
        }
      }
    );
  }

  onMapClick() {
    // e.g., open add-marker dialog if desired
  }

  onOverlayClick(event) {
    const isMarker = typeof event.overlay?.getTitle === "function";
    if (!isMarker) return;

    const rawTitle = event.overlay.getTitle();
    const title =
      this.props.from === "LocationSearch"
        ? (rawTitle || "").split(":")[1]
        : rawTitle;

    this.infoWindow = this.infoWindow || new google.maps.InfoWindow();
    this.infoWindow.setContent("<div>" + (title || "") + "</div>");
    this.infoWindow.open(event.map, event.overlay);
    event.map.setCenter(event.overlay.getPosition());

    if (this.props.from === "LocationSearch" && this.props.dataToSend) {
      const res = (rawTitle || "").split(":");
      this.props.history.push({
        pathname: "/location-history",
        state: { detail: this.props.dataToSend, imei: res[0] },
      });
    }
  }

  handleDragEnd() {
    if (this.growl?.show) {
      this.growl.show({
        severity: "info",
        summary: "Marker Dragged",
        detail: "Marker moved",
      });
    }
  }

  addMarker() {
    if (!this.state.selectedPosition || !this.state.map) return;

    const newMarker = new google.maps.Marker({
      position: {
        lat: this.state.selectedPosition.lat(),
        lng: this.state.selectedPosition.lng(),
      },
      title: this.state.markerTitle,
      map: this.state.map,
      draggable: this.state.draggableMarker,
    });

    this.setState((prev) => ({
      overlays: [...(prev.overlays || []), newMarker],
      dialogVisible: false,
      draggableMarker: false,
      markerTitle: "",
    }));
  }

  onHide() {
    this.setState({ dialogVisible: false });
  }

  render() {
    const footer = (
      <div>
        <Button label="Yes" icon="pi pi-check" onClick={this.addMarker} />
        <Button label="No" icon="pi pi-times" onClick={this.onHide} />
      </div>
    );

    return (
      <div className="content-section implementation">
        {this.state.googleMapsReady && (
          <GMap
            ref={this.myRef}
            overlays={this.state.overlays}           // markers only
            options={this.state.options}
            style={{ width: "100%", minHeight: "320px" }}
            onMapReady={this.onMapReady}
            onMapClick={this.onMapClick}
            onOverlayClick={this.onOverlayClick}
            onOverlayDragEnd={this.handleDragEnd}
          />
        )}

        <Dialog
          header="New Location"
          visible={this.state.dialogVisible}
          modal
          footer={footer}
          onHide={this.onHide}
          style={{ width: 300 }}
        >
          <div className="p-grid p-fluid">
            <div className="p-col-2" style={{ paddingTop: ".75em" }}>
              <label htmlFor="title">Label</label>
            </div>
            <div className="p-col-10">
              <InputText
                type="text"
                id="title"
                value={this.state.markerTitle}
                onChange={(e) => this.setState({ markerTitle: e.target.value })}
              />
            </div>

            <div className="p-col-2" style={{ paddingTop: ".75em" }}>
              Lat
            </div>
            <div className="p-col-10">
              <InputText
                readOnly
                value={
                  this.state.selectedPosition
                    ? this.state.selectedPosition.lat()
                    : ""
                }
              />
            </div>

            <div className="p-col-2" style={{ paddingTop: ".75em" }}>
              Lng
            </div>
            <div className="p-col-10">
              <InputText
                readOnly
                value={
                  this.state.selectedPosition
                    ? this.state.selectedPosition.lng()
                    : ""
                }
              />
            </div>

            <div className="p-col-2" style={{ paddingTop: ".75em" }}>
              <label htmlFor="drg">Drag</label>
            </div>
            <div className="p-col-10">
              <Checkbox
                checked={this.state.draggableMarker}
                onChange={(event) =>
                  this.setState({ draggableMarker: event.checked })
                }
              />
            </div>
          </div>
        </Dialog>
      </div>
    );
  }
}